import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuExternalLink, LuFile, LuFileImage, LuFileSpreadsheet, LuFileText, LuFolderOpen, LuLoaderCircle, LuScrollText, LuTrash2, LuUpload } from 'react-icons/lu';
import { storage, db } from '../../../config/firebaseConfig';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useProjectFinanceText from './projectFinanceText';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx';

const fileMeta = (fileName) => {
  const extension = String(fileName).split('.').pop().toLowerCase();
  if (extension === 'pdf') return { icon: LuFileText, tint: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300' };
  if (['doc', 'docx'].includes(extension)) return { icon: LuFileText, tint: 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-300' };
  if (['xls', 'xlsx', 'csv'].includes(extension)) return { icon: LuFileSpreadsheet, tint: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' };
  if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return { icon: LuFileImage, tint: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300' };
  return { icon: LuFile, tint: 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300' };
};

/** Firebase Storage link opened for viewing (PDF via Google Docs Viewer, others inline), as before. */
const viewUrlOf = (contract) => {
  let viewUrl = contract.url;
  if (contract.url.includes('firebasestorage.googleapis.com')) {
    const fileName = contract.name.toLowerCase();
    if (fileName.endsWith('.pdf')) {
      viewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(contract.url)}&embedded=true`;
    } else {
      viewUrl = contract.url.replace('&response-content-disposition=attachment', '');
      if (!viewUrl.includes('response-content-disposition')) {
        const separator = viewUrl.includes('?') ? '&' : '?';
        viewUrl = `${viewUrl}${separator}response-content-disposition=inline`;
      }
    }
  }
  return viewUrl;
};

/**
 * Sözleşme Yönetimi: the project's contract files (Projects.contracts,
 * Storage projects/<id>/contracts/), uploaded by drag & drop or picking,
 * opened in a new tab and removed after a confirmation.
 */
const ContractsModal = ({ onClose, project }) => {
  const { language, locale, label } = useProjectFinanceText();
  const [uploadedContracts, setUploadedContracts] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [toDelete, setToDelete] = useState(null); // index
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchUploadedContracts = useCallback(async () => {
    try {
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();
      if (projectData.contracts) setUploadedContracts(projectData.contracts);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  }, [project.id]);

  useEffect(() => {
    fetchUploadedContracts();
  }, [fetchUploadedContracts]);

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    try {
      const fileRef = ref(storage, `projects/${project.id}/contracts/${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      const projectRef = doc(db, 'Projects', project.id);
      const newContract = {
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date().toISOString(),
      };

      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();
      if (projectData.contracts) {
        await updateDoc(projectRef, { contracts: [...projectData.contracts, newContract] });
      } else {
        await updateDoc(projectRef, { contracts: [newContract] });
      }

      await fetchUploadedContracts();
      toast.success(label('contracts.uploaded', { name: file.name }));
    } catch (error) {
      console.error('Error uploading contract:', error);
      toast.error(label('contracts.uploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!isUploading && event.dataTransfer.files.length > 0) handleFileUpload(event.dataTransfer.files[0]);
  };

  const handleFileSelect = (event) => {
    if (event.target.files.length > 0) handleFileUpload(event.target.files[0]);
    event.target.value = '';
  };

  const confirmDelete = async () => {
    if (toDelete === null) return;
    setDeleting(true);
    try {
      const updatedContracts = uploadedContracts.filter((_, index) => index !== toDelete);
      await updateDoc(doc(db, 'Projects', project.id), { contracts: updatedContracts });
      setUploadedContracts(updatedContracts);
      toast.success(label('contracts.deleted'));
      setToDelete(null);
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error(label('contracts.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const openContract = (contract) => {
    const newWindow = window.open(viewUrlOf(contract), '_blank');
    if (newWindow) newWindow.focus();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(dateString));
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="lg"
        dismissible={!isUploading}
        title={label('contracts.title')}
        subtitle={project.titleShort}
        closeLabel={label('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
            <LuScrollText aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={<Button variant="ghost" onClick={onClose} disabled={isUploading}>{label('common.close')}</Button>}
      >
        <div className="tw-space-y-5">
          <div
            role="button"
            tabIndex={0}
            aria-disabled={isUploading}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && !isUploading) {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-8 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-violet-500/40 ${dragging ? 'tw-border-violet-400 tw-bg-violet-500/[0.07]' : 'tw-border-slate-300 hover:tw-border-violet-300 hover:tw-bg-violet-500/[0.03] dark:tw-border-white/15'}`}
          >
            <span className="tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-full tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300">
              {isUploading ? <LuLoaderCircle aria-hidden="true" className="tw-h-6 tw-w-6 tw-animate-spin" /> : <LuUpload aria-hidden="true" className="tw-h-6 tw-w-6" />}
            </span>
            <p className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{isUploading ? label('contracts.uploading') : label('contracts.drop')}</p>
            <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('contracts.dropHint')}</p>
            <input ref={fileInputRef} type="file" accept={ACCEPT} onChange={handleFileSelect} disabled={isUploading} className="tw-hidden" />
          </div>

          <section aria-label={label('contracts.list')}>
            <h3 className="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
              <LuFolderOpen aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-500" />
              {label('contracts.list')} · {uploadedContracts.length}
            </h3>
            {uploadedContracts.length === 0 ? (
              <p className="tw-rounded-2xl tw-bg-slate-900/[0.03] tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-bg-white/[0.03] dark:tw-text-slate-400">{label('contracts.empty')}</p>
            ) : (
              <ul className="tw-grid tw-gap-2 sm:tw-grid-cols-2">
                {uploadedContracts.map((contract, index) => {
                  const { icon: Icon, tint } = fileMeta(contract.name);
                  return (
                    <li key={`${contract.name}-${index}`} className="tw-flex tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                      <button
                        type="button"
                        onClick={() => openContract(contract)}
                        title={label('contracts.open')}
                        className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2.5 tw-rounded-xl tw-p-1 tw-text-left hover:tw-bg-slate-900/[0.03] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-violet-500/40 dark:hover:tw-bg-white/[0.04]"
                      >
                        <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tint}`}>
                          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
                        </span>
                        <span className="tw-min-w-0">
                          <span className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-slate-900 dark:tw-text-white">
                            <span className="tw-truncate">{contract.name}</span>
                            <LuExternalLink aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0 tw-opacity-50" />
                          </span>
                          {contract.uploadedAt && <span className="tw-block tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">{formatDate(contract.uploadedAt)}</span>}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setToDelete(index)}
                        title={label('contracts.delete')}
                        aria-label={label('contracts.delete')}
                        className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-rose-600 hover:tw-bg-rose-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-500/40 dark:tw-text-rose-300"
                      >
                        <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={toDelete !== null}
        title={label('contracts.deleteTitle')}
        message={label('contracts.deleteMessage', { name: toDelete !== null ? uploadedContracts[toDelete]?.name || '' : '' })}
        confirmLabel={label('contracts.delete')}
        cancelLabel={label('common.cancel')}
        tone="danger"
        icon={LuTrash2}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setToDelete(null)}
      />
    </>
  );
};

ContractsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired,
};

export default ContractsModal;
